/** @format */
import angular from 'angular';
import { of } from 'rxjs';
import { take, tap, switchMap } from 'rxjs/operators';
import { authModule, ModalService } from 'sis-common';
import { customStudyDraftSummaryModule } from '../customStudyDraft/customStudyDraftSummary.component';
import { localDateRangeFilterModule } from '../date/filters/localDateRange/localDateRange.filter';
import { errorServiceModule } from '../error-handler/legacy/errorService';
import { applicationDecisionModelModule } from '../model/applicationDecision.model';
import { studentApplicationModelModule } from '../model/studentApplication.model';
import { commonApplicationDecisionServiceModule } from '../service/applicationDecision.service';
import { commonModuleContentApplicationServiceModule } from '../service/moduleContentApplication.service';
import { personNameServiceModule } from '../service/personName.service';
import { PrivatePersonBasicInfoEntityService } from '../service/private-person-basic-info-entity.service';
import { commonStudentServiceModule } from '../service/student.service';
import { commonStudentApplicationServiceModule } from '../service/studentApplication.service';
import { WorkflowEntityService } from '../service/workflow-entity.service';
import { CommonShowModuleContentApplicationModalComponent } from './commonShowModuleContentApplicationModal.component';
import commonShowModuleContentApplicationModalTpl from './commonShowModuleContentApplicationModal.tpl.html';
import { moduleContentApplicationDecisionSummaryModule } from './moduleContentApplicationDecisionSummary.component';
import { moduleContentApplicationStructureModule } from './moduleContentApplicationStructure.component';
import { studentApplicationStateBadgeModule } from './studentApplicationStateBadge.dir';

commonShowModuleContentApplicationModalController.$inject = [
    '$q',
    '$scope',
    'commonStudentService',
    'defaultPromiseHandler',
    'STUDENT_APPLICATION_STATE',
    'moduleContentApplicationService',
    'commonApplicationDecisionService',
    'applicationDecisionJSDataModel',
    'workflowEntityService',
    'privatePersonBasicInfoEntityService',
    'personNameService',
];
commonShowModuleContentApplicationModalService.$inject = ['modalService', '$q'];

export const commonShowModuleContentApplicationModalModule = 'sis-components.applications.commonShowModuleContentApplicationModal';

angular
    .module(commonShowModuleContentApplicationModalModule, [
        authModule,
        localDateRangeFilterModule,
        commonStudentServiceModule,
        moduleContentApplicationStructureModule,
        moduleContentApplicationDecisionSummaryModule,
        studentApplicationStateBadgeModule,
        commonStudentApplicationServiceModule,
        errorServiceModule,
        studentApplicationModelModule,
        commonModuleContentApplicationServiceModule,
        applicationDecisionModelModule,
        commonApplicationDecisionServiceModule,
        customStudyDraftSummaryModule,
        personNameServiceModule,
        ModalService.downgrade.moduleName,
        WorkflowEntityService.downgrade.moduleName,
        PrivatePersonBasicInfoEntityService.downgrade.moduleName,
    ])
    .factory('commonShowModuleContentApplicationModal', commonShowModuleContentApplicationModalService)
    .component('commonShowModuleContentApplicationModal', {
        bindings: {
            close: '&',
            resolve: '<',
        },
        controller: commonShowModuleContentApplicationModalController,
        template: commonShowModuleContentApplicationModalTpl,
    });

function commonShowModuleContentApplicationModalService(modalService, $q) {
    return {
        open: (studentId, approvalRequest) =>
            modalService
                .open(CommonShowModuleContentApplicationModalComponent, { approvalRequest, studentId }, { size: 'lg' })
                .result.catch(() => $q.reject()),
    };
}

function commonShowModuleContentApplicationModalController(
    $q,
    $scope,
    commonStudentService,
    defaultPromiseHandler,
    STUDENT_APPLICATION_STATE,
    moduleContentApplicationService,
    commonApplicationDecisionService,
    applicationDecisionJSDataModel,
    workflowEntityService,
    privatePersonBasicInfoEntityService,
    personNameService,
) {
    const $ctrl = this;

    $ctrl.$onInit = () => {
        commonStudentService
            .findStudent($ctrl.resolve.studentId)
            .then(student => {
                $ctrl.student = student;
            })
            .catch(defaultPromiseHandler.loggingRejectedPromiseHandler);

        $ctrl.approvalRequest = $ctrl.resolve.approvalRequest;

        moduleContentApplicationService
            .loadModuleContentApplicationObjects($ctrl.approvalRequest)
            .then(result => {
                $ctrl.module = result.module;
                $ctrl.selectedCustomCourseUnitAttainments = result.selectedCustomCourseUnitAttainments;
                $ctrl.selectedCustomModuleAttainments = result.selectedCustomModuleAttainments;
                $ctrl.education = result.education;
                $ctrl.plan = result.plan;
                $ctrl.selectedModules = result.selectedModules;
                $ctrl.selectedModuleAttainmentsByGroupId = result.selectedModuleAttainmentsByGroupId;
                $ctrl.selectedGroupingModules = result.selectedGroupingModules;
                $ctrl.selectedCourseUnits = result.selectedCourseUnits;
                $ctrl.selectedCourseUnitAttainmentsByGroupId = result.selectedCourseUnitAttainmentsByGroupId;
                $ctrl.parentModule = result.parentModule;
                $ctrl.gradeScalesById = result.gradeScalesById;
                $ctrl.customStudyDrafts = $ctrl.approvalRequest.customStudyDrafts;
            })
            .catch(defaultPromiseHandler.loggingRejectedPromiseHandler);

        $ctrl.decision = undefined;

        commonApplicationDecisionService.findByApplicationId($ctrl.approvalRequest.id).then(decision => {
            if (decision) {
                $ctrl.decision = applicationDecisionJSDataModel.loadRelations(decision, ['approver', 'formalAssessor', 'contentAssessor']);
            }
        });

        getWorkflow($ctrl.approvalRequest.id);

        $ctrl.badgeClass = getBadgeClass($ctrl.approvalRequest);
    };

    function getWorkflow(applicationId) {
        workflowEntityService
            .getById(applicationId, { bypassCache: true })
            .pipe(
                take(1),
                tap(workflow => {
                    $ctrl.workflow = workflow;
                }),
                switchMap(workflow => {
                    if (workflow.initiatorType === 'STAFF') {
                        return privatePersonBasicInfoEntityService.getById(workflow.createdByPersonId);
                    }
                    return of(undefined);
                }),
                take(1),
                tap(person => {
                    if (person) {
                        $ctrl.initiatorPersonFullName = personNameService.getFullName(person);
                    }
                }),
            )
            // Ensure change detection is run after finished.
            .subscribe(() => $scope.$evalAsync(), defaultPromiseHandler.loggingRejectedPromiseHandler);
    }

    function getBadgeClass(studentApplication) {
        switch (studentApplication.state) {
            case STUDENT_APPLICATION_STATE.REQUESTED:
            case STUDENT_APPLICATION_STATE.IN_HANDLING:
            case STUDENT_APPLICATION_STATE.CANCELLED:
            case STUDENT_APPLICATION_STATE.REVOKED:
                return 'secondary';
            case STUDENT_APPLICATION_STATE.REJECTED:
                return 'danger';
            default:
                return 'success';
        }
    }
}
